import { useEffect, useRef } from "react";

/**
 * Matrix-style falling katakana/glyph rain background.
 * Fixed-position canvas behind all content. Lightweight (single rAF loop).
 */
export default function MatrixRain({ density = 0.95, speed = 1, opacity = 0.18 }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let drops = [];
    let raf;
    let columnCount;

    const chars = "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜｦﾝ0123456789ABCDEF$#@%&*+-=<>/";

    const setup = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const fontSize = 14;
      columnCount = Math.floor(canvas.width / fontSize);
      drops = new Array(columnCount).fill(0).map(() => Math.random() * -canvas.height);
      ctx.font = `${fontSize}px JetBrains Mono, monospace`;
    };

    const draw = () => {
      ctx.fillStyle = "rgba(2, 5, 3, 0.085)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = `rgba(0, 255, 122, ${opacity})`;

      for (let i = 0; i < drops.length; i++) {
        if (Math.random() > density) continue;
        const ch = chars[Math.floor(Math.random() * chars.length)];
        const x = i * 14;
        const y = drops[i];
        // bright head
        ctx.fillStyle = `rgba(214, 255, 217, ${opacity * 4})`;
        ctx.fillText(ch, x, y);
        // trail
        ctx.fillStyle = `rgba(0, 255, 122, ${opacity})`;
        for (let t = 1; t < 6; t++) {
          ctx.fillText(chars[Math.floor(Math.random() * chars.length)], x, y - t * 14);
        }
        drops[i] += 14 * speed;
        if (drops[i] > canvas.height + 100) {
          drops[i] = Math.random() * -200;
        }
      }
      raf = requestAnimationFrame(draw);
    };

    setup();
    draw();

    const onResize = () => setup();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [density, speed, opacity]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
      aria-hidden="true"
    />
  );
}
